import React, { useState, useEffect } from 'react';
import { X, PawPrint } from 'lucide-react';

/**
 * Tema del Día Internacional del Perro.
 *
 * - Banner cálido con mensaje y botón cerrar (persistente en localStorage)
 * - Animación sutil de huellitas cayendo (overlay sin bloqueo de clics)
 * - Auto-oculta después de MOTHERS_DAY_END_DATE (aquí: mañana + 1)
 *
 * Para desactivarlo manualmente, ajustar DOG_DAY_END_DATE a una fecha pasada.
 */

// Fecha final (inclusive). Después de esta fecha, el tema deja de mostrarse.
const DOG_DAY_END_DATE = '2026-07-23'; // Auto-oculta el 24 de julio 2026
const DISMISS_STORAGE_KEY = 'dogDay2026_dismissed';
const PAW_COUNT = 16;

const generatePaws = () => {
  const paws = [];
  const colors = ['#a16207', '#b45309', '#92400e', '#d97706', '#78350f'];
  for (let i = 0; i < PAW_COUNT; i++) {
    paws.push({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 14,
      duration: 16 + Math.random() * 10,
      size: 18 + Math.random() * 14,
      color: colors[Math.floor(Math.random() * colors.length)],
      opacity: 0.35 + Math.random() * 0.35,
      drift: -40 + Math.random() * 80,
      rotate: Math.floor(Math.random() * 360),
    });
  }
  return paws;
};

const PAWS = generatePaws();

const isThemeActive = () => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(DOG_DAY_END_DATE);
    return today <= end;
  } catch {
    return false;
  }
};

export default function DogDayTheme() {
  const [bannerVisible, setBannerVisible] = useState(false);

  useEffect(() => {
    if (!isThemeActive()) return;
    const dismissed = localStorage.getItem(DISMISS_STORAGE_KEY) === 'true';
    if (!dismissed) setBannerVisible(true);
  }, []);

  if (!isThemeActive()) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_STORAGE_KEY, 'true');
    setBannerVisible(false);
  };

  return (
    <>
      {/* Banner superior */}
      {bannerVisible && (
        <div
          data-testid="dog-day-banner"
          className="relative w-full bg-gradient-to-r from-amber-100 via-orange-200 to-amber-100 border-b border-amber-300/70 shadow-sm"
        >
          <div className="container mx-auto px-4 py-2 flex items-center justify-center relative">
            <div className="flex items-center gap-2 text-amber-900">
              <PawPrint className="h-4 w-4 fill-amber-700 text-amber-700 -rotate-12" />
              <span className="text-sm sm:text-base font-medium tracking-wide">
                Feliz Dia Internacional del Perro &mdash; <span className="font-semibold">SIEMPRE</span> celebra a nuestros mejores amigos
              </span>
              <PawPrint className="h-4 w-4 fill-amber-700 text-amber-700 rotate-12" />
            </div>
            <button
              data-testid="dog-day-banner-close"
              onClick={handleDismiss}
              aria-label="Cerrar mensaje del Dia del Perro"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-white/50 transition-colors text-amber-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Overlay de huellitas cayendo */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-[60] overflow-hidden"
        data-testid="dog-day-paws"
      >
        {PAWS.map((p) => (
          <span
            key={p.id}
            className="dd-paw"
            style={{
              left: `${p.left}%`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              color: p.color,
              opacity: p.opacity,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
              '--dd-drift': `${p.drift}px`,
              '--dd-rotate': `${p.rotate}deg`,
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="100%" height="100%">
              <path d="M12 14c-1.5 0-3 1-3 3s1.5 3 3 3 3-1 3-3-1.5-3-3-3zM6.5 9C5.7 9 5 9.9 5 11s.7 2 1.5 2S8 12.1 8 11s-.7-2-1.5-2zm11 0C16.7 9 16 9.9 16 11s.7 2 1.5 2 1.5-.9 1.5-2-.7-2-1.5-2zM9 6C8.2 6 7.5 6.9 7.5 8S8.2 10 9 10s1.5-.9 1.5-2S9.8 6 9 6zm6 0c-.8 0-1.5.9-1.5 2s.7 2 1.5 2 1.5-.9 1.5-2S15.8 6 15 6z"/>
            </svg>
          </span>
        ))}
      </div>
    </>
  );
}
